import React from 'react';
import MentionHoverCard from './entityMentions/MentionHoverCard';
import {isLinkedEntityRef} from '../utils/entityLinks';
import {readUnlinkedMentionsDisplayedSync} from '../hooks/entityDisplayConfig';

/**
 * A segment entity renders "linked" (blue) when it resolves to a Wikidata QID or custom
 * D ref, matching the report-editor/preview scheme.
 *
 * QID source: the backend `/entities/segments/{id}/mappings` endpoint returns
 * `mapping.entity.wikidataId` — set by the NER/NEL annotation step, NOT by enrichment.
 * Enrichment (fetching descriptions, images etc.) runs asynchronously and must NOT
 * gate whether an entity appears as "linked" (blue) vs "mention" (yellow).
 *
 * Recognised spans without a linked ref — dates, amounts, unresolved names — render yellow.
 */
function isLinkedEntity(entity) {
    if (!entity) return false;
    const candidates = [entity.wikidataId, entity.entityId, entity.localEntityRef];
    return candidates.some((c) => c != null && isLinkedEntityRef(String(c)));
}

function linkedEntityRef(entity) {
    if (!entity) return null;
    const candidates = [entity.wikidataId, entity.entityId, entity.localEntityRef];
    for (const candidate of candidates) {
        if (candidate != null && isLinkedEntityRef(String(candidate))) {
            return String(candidate).trim();
        }
    }
    return null;
}

/**
 * Highlights entities within text using startOffset/endOffset from backend entity recognition.
 * Wraps each entity in a <span> with wikidata-mention styling and data attributes.
 *
 * @param {string} text - The segment text
 * @param {Array} entities - Array of entity objects from backend with structure:
 *   {entityId, entityType, canonicalName, description, confidence, startOffset, endOffset, ...}
 * @param {Function} onEntityClick - Optional click handler that receives the entity object
 * @returns {React.ReactNode} - Text with entity highlights
 */
export function highlightEntities(text = '', entities = [], onEntityClick = null) {
    if (!text || !entities || entities.length === 0) {
        return text;
    }

    // Validate and filter entities
    const validEntities = entities.filter(e =>
        e &&
        typeof e.startOffset === 'number' &&
        typeof e.endOffset === 'number' &&
        e.startOffset >= 0 &&
        e.endOffset > e.startOffset &&
        e.endOffset <= text.length
    );

    if (validEntities.length === 0) {
        return text;
    }

    // Sort entities by startOffset to process them in order
    const sortedEntities = [...validEntities].sort((a, b) => a.startOffset - b.startOffset);

    // Check for overlapping entities and resolve by taking the first one at each position
    const nonOverlapping = [];
    let lastEnd = -1;
    for (const entity of sortedEntities) {
        if (entity.startOffset >= lastEnd) {
            nonOverlapping.push(entity);
            lastEnd = entity.endOffset;
        }
    }

    // Build the result as an array of React elements and strings
    const result = [];
    let currentPos = 0;

    nonOverlapping.forEach((entity, idx) => {
        // Add plain text before this entity
        if (currentPos < entity.startOffset) {
            result.push(text.slice(currentPos, entity.startOffset));
        }

        // Extract the entity text
        const entityText = text.slice(entity.startOffset, entity.endOffset);

        // Build data attributes for the span
        const dataAttrs = {
            'data-entity-id': entity.entityId || '',
            'data-wikidata-id': entity.wikidataId || '',
            'data-entity-type': entity.entityType || '',
            'data-canonical-name': entity.canonicalName || '',
            'data-description': entity.description || '',
            'data-confidence': entity.confidence != null ? String(entity.confidence) : '',
            'data-ner-score': entity.nerScore != null ? String(entity.nerScore) : '',
            'data-linking-score': entity.linkingScore != null ? String(entity.linkingScore) : '',
        };

        // A KB-linked entity (resolves to a real Wikidata QID) renders blue,
        // exactly like the editor/preview. Unlinked spans get the yellow-underline mention style.
        const linked = isLinkedEntity(entity);
        if (!linked && !readUnlinkedMentionsDisplayedSync()) {
            result.push(entityText);
            currentPos = entity.endOffset;
            return;
        }

        const className = linked
            ? 'wikidata-entity wikidata-entity--linked'
            : 'wikidata-entity wikidata-entity--mention';

        // Add the highlighted entity span
        result.push(
            <MentionHoverCard
                key={`entity-${idx}-${entity.startOffset}`}
                name={entity.canonicalName || entityText}
                entityType={entity.entityType}
                entityRef={linkedEntityRef(entity)}
                confidence={entity.confidence}
                nerScore={entity.nerScore}
                linkingScore={entity.linkingScore}
            >
                <span
                    className={className}
                    {...dataAttrs}
                    onClick={onEntityClick ? (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onEntityClick(entity, e.currentTarget);
                    } : undefined}
                    style={{
                        cursor: onEntityClick ? 'pointer' : 'default'
                    }}
                >
                    {entityText}
                </span>
            </MentionHoverCard>
        );

        currentPos = entity.endOffset;
    });

    // Add any remaining text after the last entity
    if (currentPos < text.length) {
        result.push(text.slice(currentPos));
    }

    return result;
}

/**
 * Marks matched spans (plain startOffset/endOffset, no entity semantics) with a yellow
 * highlighter-pen style, then applies query-term bolding to the rest of the text.
 *
 * Used for highlight (bookmark) search hits: the span here is the user's own previously-saved
 * highlighted excerpt projected onto its surrounding context snippet (see
 * `EntitySnippetHighlighter` / `HighlightSearchAdapter` on the backend) — it is NOT a
 * recognized NER/NEL entity, so it must not render with the wikidata-entity blue/yellow-underline
 * convention (which implies "this is a linkable entity" and carries a hover card). A plain
 * `<mark>` with a yellow background reads as "this is the part you highlighted", matching how
 * highlighters work everywhere else in the product.
 *
 * @param {string} text - The segment text
 * @param {Array} spans - Array of `{startOffset, endOffset}` ranges (entities-shaped, extra
 *   fields like wikidataId/canonicalName are ignored)
 * @param {string} query - Search query for term highlighting on the non-marked text
 * @param {Function} highlightTextFn - The highlightText function for query terms
 * @returns {React.ReactNode}
 */
export function highlightMarkedSpansAndQuery(text = '', spans = [], query = '', highlightTextFn = null) {
    if (!text) return text;

    const validSpans = (spans || []).filter(s =>
        s &&
        typeof s.startOffset === 'number' &&
        typeof s.endOffset === 'number' &&
        s.startOffset >= 0 &&
        s.endOffset > s.startOffset &&
        s.endOffset <= text.length
    );

    if (validSpans.length === 0) {
        return highlightTextFn ? highlightTextFn(text, query) : text;
    }

    const sorted = [...validSpans].sort((a, b) => a.startOffset - b.startOffset);
    const nonOverlapping = [];
    let lastEnd = -1;
    for (const span of sorted) {
        if (span.startOffset >= lastEnd) {
            nonOverlapping.push(span);
            lastEnd = span.endOffset;
        }
    }

    const applyQuery = (chunk, keyPrefix) => {
        const highlighted = highlightTextFn && query ? highlightTextFn(chunk, query) : chunk;
        if (Array.isArray(highlighted)) {
            return highlighted.map((node, i) =>
                React.isValidElement(node) ? React.cloneElement(node, {key: `${keyPrefix}-${i}`}) : node
            );
        }
        return [highlighted];
    };

    const result = [];
    let currentPos = 0;

    nonOverlapping.forEach((span, idx) => {
        if (currentPos < span.startOffset) {
            result.push(...applyQuery(text.slice(currentPos, span.startOffset), `q-${currentPos}`));
        }

        result.push(
            <mark key={`mark-${idx}-${span.startOffset}`} className="search-snippet-mark">
                {text.slice(span.startOffset, span.endOffset)}
            </mark>
        );

        currentPos = span.endOffset;
    });

    if (currentPos < text.length) {
        result.push(...applyQuery(text.slice(currentPos), 'q-end'));
    }

    return result;
}

/**
 * Combines entity highlighting with query term highlighting.
 * First applies entity highlighting, then applies query highlighting to non-entity text.
 *
 * @param {string} text - The segment text (or chunk if textOffset is provided)
 * @param {Array} entities - Array of entity objects with startOffset/endOffset relative to the full segment
 * @param {string} query - Search query for term highlighting
 * @param {Function} highlightTextFn - The highlightText function for query terms
 * @param {Function} onEntityClick - Optional click handler for entities
 * @param {number} textOffset - Optional offset of this text chunk within the full segment (default 0)
 * @returns {React.ReactNode} - Text with both entity and query highlights
 */
export function highlightEntitiesAndQuery(text = '', entities = [], query = '', highlightTextFn = null, onEntityClick = null, textOffset = 0) {
    if (!text) return text;

    // If no entities, just use query highlighting
    if (!entities || entities.length === 0) {
        return highlightTextFn ? highlightTextFn(text, query) : text;
    }

    const textEnd = textOffset + text.length;

    // Filter entities that overlap with this text chunk and adjust their offsets
    // Entity offsets are relative to the full segment, we need to convert to chunk-relative
    const validEntities = entities
        .filter(e =>
            e &&
            typeof e.startOffset === 'number' &&
            typeof e.endOffset === 'number' &&
            e.startOffset >= 0 &&
            e.endOffset > e.startOffset &&
            // Entity must overlap with this chunk
            e.startOffset < textEnd &&
            e.endOffset > textOffset
        )
        .map(e => {
            // Adjust offsets to be relative to the chunk
            const adjustedStart = Math.max(0, e.startOffset - textOffset);
            const adjustedEnd = Math.min(text.length, e.endOffset - textOffset);
            return {
                ...e,
                startOffset: adjustedStart,
                endOffset: adjustedEnd,
                // Keep original offsets for reference if needed
                _originalStartOffset: e.startOffset,
                _originalEndOffset: e.endOffset
            };
        })
        .filter(e => e.endOffset > e.startOffset); // Ensure valid range after adjustment

    if (validEntities.length === 0) {
        return highlightTextFn ? highlightTextFn(text, query) : text;
    }

    // Sort and remove overlaps
    const sortedEntities = [...validEntities].sort((a, b) => a.startOffset - b.startOffset);
    const nonOverlapping = [];
    let lastEnd = -1;
    for (const entity of sortedEntities) {
        if (entity.startOffset >= lastEnd) {
            nonOverlapping.push(entity);
            lastEnd = entity.endOffset;
        }
    }

    // Build result with both entity and query highlighting
    const result = [];
    let currentPos = 0;

    nonOverlapping.forEach((entity, idx) => {
        // Add text before entity with query highlighting
        if (currentPos < entity.startOffset) {
            const plainText = text.slice(currentPos, entity.startOffset);
            const highlighted = highlightTextFn && query ? highlightTextFn(plainText, query) : plainText;
            if (Array.isArray(highlighted)) {
                result.push(...highlighted.map((node, i) =>
                    React.isValidElement(node)
                        ? React.cloneElement(node, {key: `q-${currentPos}-${i}`})
                        : node
                ));
            } else {
                result.push(highlighted);
            }
        }

        // Add entity span (entities take precedence over query highlights)
        const entityText = text.slice(entity.startOffset, entity.endOffset);
        const dataAttrs = {
            'data-entity-id': entity.entityId || '',
            'data-wikidata-id': entity.wikidataId || '',
            'data-entity-type': entity.entityType || '',
            'data-canonical-name': entity.canonicalName || '',
            'data-description': entity.description || '',
            'data-confidence': entity.confidence != null ? String(entity.confidence) : '',
            'data-ner-score': entity.nerScore != null ? String(entity.nerScore) : '',
            'data-linking-score': entity.linkingScore != null ? String(entity.linkingScore) : '',
        };

        const linked = isLinkedEntity(entity);
        if (!linked && !readUnlinkedMentionsDisplayedSync()) {
            result.push(entityText);
            currentPos = entity.endOffset;
            return;
        }

        const className = linked
            ? 'wikidata-entity wikidata-entity--linked'
            : 'wikidata-entity wikidata-entity--mention';

        result.push(
            <MentionHoverCard
                key={`entity-${idx}-${entity.startOffset}`}
                name={entity.canonicalName || entityText}
                entityType={entity.entityType}
                entityRef={linkedEntityRef(entity)}
                confidence={entity.confidence}
                nerScore={entity.nerScore}
                linkingScore={entity.linkingScore}
            >
                <span
                    className={className}
                    {...dataAttrs}
                    onClick={onEntityClick ? (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onEntityClick(entity, e.currentTarget);
                    } : undefined}
                    style={{
                        cursor: onEntityClick ? 'pointer' : 'default'
                    }}
                >
                    {entityText}
                </span>
            </MentionHoverCard>
        );

        currentPos = entity.endOffset;
    });

    // Add remaining text with query highlighting
    if (currentPos < text.length) {
        const plainText = text.slice(currentPos);
        const highlighted = highlightTextFn && query ? highlightTextFn(plainText, query) : plainText;
        if (Array.isArray(highlighted)) {
            result.push(...highlighted.map((node, i) =>
                React.isValidElement(node)
                    ? React.cloneElement(node, {key: `q-end-${i}`})
                    : node
            ));
        } else {
            result.push(highlighted);
        }
    }

    return result;
}

